/**
 * OnboardingWizard - Modular onboarding flow for new users
 *
 * Steps (modular - easily add more):
 * 1. AcceptTerms - Accept terms of service and acceptable use policy
 * 2. ChooseSubscription - Select a subscription plan
 * 3. (Future) EmailVerification - Verify email address
 * 4. (Future) Tutorial - Product tour
 *
 * The wizard tracks progress and can be resumed if user drops off.
 */
import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../../design-system';

// Step registry - add new steps here
import AcceptTermsStep from './steps/AcceptTermsStep';
import ChooseSubscriptionStep from './steps/ChooseSubscriptionStep';

// Define the step order and configuration
const ONBOARDING_STEPS = [
  {
    id: 'accept-terms',
    component: AcceptTermsStep,
    title: 'Terms of Service',
    required: true,
  },
  {
    id: 'choose-subscription',
    component: ChooseSubscriptionStep,
    title: 'Choose Your Plan',
    required: true,
  },
  // Future steps:
  // { id: 'verify-email', component: VerifyEmailStep, title: 'Verify Email', required: false },
  // { id: 'tutorial', component: TutorialStep, title: 'Quick Tour', required: false },
];

// Context for sharing wizard state with steps
const OnboardingContext = createContext(null);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingWizard');
  }
  return context;
};

const OnboardingWizard = ({
  userInfo = {},       // { email, provider, isNewUser }
  onComplete,          // Called when wizard finishes
  onSkip,              // Called if user skips (optional)
  startAtStep = 0,     // Resume from specific step
}) => {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(startAtStep);
  const [stepData, setStepData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Get current step config
  const currentStep = ONBOARDING_STEPS[currentStepIndex];
  const StepComponent = currentStep?.component;
  const totalSteps = ONBOARDING_STEPS.length;
  const isLastStep = currentStepIndex === totalSteps - 1;

  // Save progress to session storage (for resume)
  useEffect(() => {
    sessionStorage.setItem('onboarding_progress', JSON.stringify({
      currentStepIndex,
      stepData,
      userInfo,
    }));
  }, [currentStepIndex, stepData, userInfo]);

  // Update step data
  const updateStepData = (stepId, data) => {
    setStepData(prev => ({
      ...prev,
      [stepId]: { ...prev[stepId], ...data },
    }));
  };

  // Go to next step or complete
  const goToNextStep = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  // Go to previous step
  const goToPreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  // Skip current step (if allowed)
  const skipStep = () => {
    if (!currentStep.required) {
      goToNextStep();
    }
  };

  // Complete the wizard
  const handleComplete = () => {
    // Clear saved progress
    sessionStorage.removeItem('onboarding_progress');
    sessionStorage.removeItem('check_onboarding');

    // Call completion handler
    onComplete?.(stepData);

    // Navigate to dashboard
    navigate('/dashboard');
  };

  // Handle skip all (if allowed)
  const handleSkipAll = () => {
    sessionStorage.removeItem('onboarding_progress');
    onSkip?.();
    navigate('/dashboard');
  };

  // Context value
  const contextValue = {
    userInfo,
    stepData,
    updateStepData,
    goToNextStep,
    goToPreviousStep,
    skipStep,
    isLoading,
    setIsLoading,
    currentStepIndex,
    totalSteps,
    isLastStep,
  };

  if (!StepComponent) {
    return null;
  }

  return (
    <OnboardingContext.Provider value={contextValue}>
      <div style={styles.overlay}>
        <div style={styles.container}>
          {/* Progress indicator */}
          <div style={styles.progress}>
            <div style={styles.progressBar}>
              <div
                style={{
                  ...styles.progressFill,
                  width: `${((currentStepIndex + 1) / totalSteps) * 100}%`,
                }}
              />
            </div>
            <span style={styles.progressText}>
              Step {currentStepIndex + 1} of {totalSteps}
            </span>
          </div>

          {/* Step content */}
          <div style={styles.content}>
            <StepComponent />
          </div>
        </div>
      </div>
    </OnboardingContext.Provider>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  container: {
    width: '100%',
    maxWidth: '600px',
    backgroundColor: tokens.colors.neutral[900],
    borderRadius: '16px',
    border: `1px solid ${tokens.colors.neutral[800]}`,
    overflow: 'hidden',
  },
  progress: {
    padding: '16px 24px',
    borderBottom: `1px solid ${tokens.colors.neutral[800]}`,
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  progressBar: {
    flex: 1,
    height: '4px',
    backgroundColor: tokens.colors.neutral[800],
    borderRadius: '2px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: tokens.colors.primary[500],
    borderRadius: '2px',
    transition: 'width 0.3s ease',
  },
  progressText: {
    fontSize: '12px',
    color: tokens.colors.neutral[500],
    whiteSpace: 'nowrap',
  },
  content: {
    padding: '32px',
  },
};

export default OnboardingWizard;
